/**
 * RoomPickerDropdown — a searchable dropdown for picking a laminate
 * room name. Web port of mobile's `RoomPickerSheet.tsx`.
 *
 * Behaviour:
 *   • Click the input → opens a popover below it.
 *   • Popover sections:
 *       PROJECT ROOMS — rooms already used in this project
 *                       (passed via `existingRooms`)
 *       COMMON ROOMS  — curated default list, with project rooms
 *                       filtered out (so they don't appear twice).
 *   • Typing in the input filters BOTH sections live.
 *   • If the search text has no exact match, a footer CTA
 *     "Add 'X' as room" surfaces — clicking it picks the typed value
 *     verbatim so the studio can record a custom room without leaving
 *     the form.
 *   • Pick → fires `onChange` + closes.
 *
 * No external dropdown library — pure React + CSS (positioned
 * absolutely inside a `relative` wrapper). Closes on outside-click
 * via a document mousedown listener, and on `Escape`.
 */
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

export type RoomPickerDropdownProps = {
  /** Currently-selected room (free-text). */
  value: string;
  onChange: (next: string) => void;
  /** Rooms already used in this project. Rendered as the top
   *  "PROJECT ROOMS" section. */
  existingRooms: string[];
  /** Curated default list. Rendered below as "COMMON ROOMS". */
  commonRooms: readonly string[];
  /** Optional placeholder for the input. */
  placeholder?: string;
  /** Disable interaction (e.g. when the parent form is busy). */
  disabled?: boolean;
  /** Autofocus the input on mount. */
  autoFocus?: boolean;
};

export function RoomPickerDropdown({
  value,
  onChange,
  existingRooms,
  commonRooms,
  placeholder = 'Select or type a room',
  disabled = false,
  autoFocus = false,
}: RoomPickerDropdownProps) {
  const [open, setOpen] = useState(false);
  // `query` is what the user is typing — independent from `value`
  // (the committed pick) so they can search "kitch" without
  // immediately overwriting their previous selection.
  const [query, setQuery] = useState('');
  const wrapRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);

  // Reset the query whenever the popover opens so the full list is
  // visible — typing then narrows it down.
  useEffect(() => {
    if (open) setQuery('');
  }, [open]);

  // Outside-click + Esc close.
  useEffect(() => {
    if (!open) return;
    function onDown(e: MouseEvent) {
      if (!wrapRef.current) return;
      if (e.target instanceof Node && !wrapRef.current.contains(e.target)) {
        setOpen(false);
      }
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        e.stopPropagation();
        setOpen(false);
        inputRef.current?.blur();
      }
    }
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  // De-dupe existing rooms (preserve order) + filter out any
  // appearing in common to avoid double rendering.
  const projectRooms = useMemo(() => {
    const seen = new Set<string>();
    const out: string[] = [];
    for (const r of existingRooms) {
      const k = r.trim();
      if (!k || seen.has(k.toLowerCase())) continue;
      seen.add(k.toLowerCase());
      out.push(k);
    }
    return out;
  }, [existingRooms]);

  const commonOnly = useMemo(() => {
    const usedLower = new Set(projectRooms.map((r) => r.toLowerCase()));
    return commonRooms.filter((r) => !usedLower.has(r.toLowerCase()));
  }, [commonRooms, projectRooms]);

  const q = query.trim().toLowerCase();
  const filteredProject = useMemo(
    () => (q ? projectRooms.filter((r) => r.toLowerCase().includes(q)) : projectRooms),
    [projectRooms, q],
  );
  const filteredCommon = useMemo(
    () => (q ? commonOnly.filter((r) => r.toLowerCase().includes(q)) : commonOnly),
    [commonOnly, q],
  );

  const hasExactMatch =
    !!q &&
    (projectRooms.some((r) => r.toLowerCase() === q) ||
      commonOnly.some((r) => r.toLowerCase() === q));

  const handlePick = useCallback(
    (room: string) => {
      onChange(room);
      setOpen(false);
      setQuery('');
    },
    [onChange],
  );

  // Pressing Enter inside the input commits the typed text (or the
  // first filtered match) as the room name and closes.
  function onInputKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Enter') {
      e.preventDefault();
      const firstMatch =
        filteredProject[0] ?? filteredCommon[0] ?? query.trim();
      if (firstMatch) handlePick(firstMatch);
    } else if (e.key === 'ArrowDown' && !open) {
      setOpen(true);
    }
  }

  return (
    <div ref={wrapRef} style={{ position: 'relative' }}>
      {/* Trigger: shows the picked value OR the live query. The
       *  user can type to search or click to open the full list. */}
      <div
        style={{
          position: 'relative',
          display: 'flex',
          alignItems: 'center',
        }}
      >
        <input
          ref={inputRef}
          type="text"
          value={open ? query : value}
          onChange={(e) => {
            if (!open) setOpen(true);
            setQuery(e.currentTarget.value);
          }}
          onFocus={() => setOpen(true)}
          onClick={() => setOpen(true)}
          onKeyDown={onInputKeyDown}
          placeholder={placeholder}
          disabled={disabled}
          autoFocus={autoFocus}
          spellCheck={false}
          autoComplete="off"
          style={{
            width: '100%',
            padding: '10px 36px 10px 12px',
            border: '1px solid var(--border)',
            borderRadius: 6,
            background: '#fff',
            fontSize: 13,
            color: 'var(--text-primary)',
            fontFamily: 'inherit',
            cursor: disabled ? 'not-allowed' : 'pointer',
          }}
        />
        <i
          className={`ph ${open ? 'ph-caret-up' : 'ph-caret-down'}`}
          style={{
            position: 'absolute',
            right: 12,
            fontSize: 14,
            color: 'var(--text-tertiary)',
            pointerEvents: 'none',
          }}
        />
      </div>

      {open ? (
        <div
          style={{
            position: 'absolute',
            top: 'calc(100% + 4px)',
            left: 0,
            right: 0,
            zIndex: 50,
            background: '#fff',
            border: '1px solid var(--border)',
            borderRadius: 8,
            boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
            maxHeight: 320,
            overflow: 'auto',
            padding: 4,
          }}
          // Block outer slide-over's scroll/click handlers from
          // hijacking touches inside the popover.
          onMouseDown={(e) => e.stopPropagation()}
        >
          {/* PROJECT ROOMS section */}
          {filteredProject.length > 0 ? (
            <>
              <SectionLabel>Project rooms</SectionLabel>
              {filteredProject.map((r) => (
                <RoomRow
                  key={`proj_${r}`}
                  room={r}
                  active={r.toLowerCase() === value.toLowerCase()}
                  icon="ph ph-house-line"
                  onPick={() => handlePick(r)}
                />
              ))}
            </>
          ) : null}

          {/* COMMON ROOMS section */}
          {filteredCommon.length > 0 ? (
            <>
              <SectionLabel>Common rooms</SectionLabel>
              {filteredCommon.map((r) => (
                <RoomRow
                  key={`common_${r}`}
                  room={r}
                  active={r.toLowerCase() === value.toLowerCase()}
                  icon="ph ph-folder-simple"
                  onPick={() => handlePick(r)}
                />
              ))}
            </>
          ) : null}

          {/* No-match state */}
          {q && filteredProject.length === 0 && filteredCommon.length === 0 ? (
            <div
              style={{
                padding: '12px 14px',
                fontSize: 12,
                color: 'var(--text-tertiary)',
              }}
            >
              No matching room.
            </div>
          ) : null}

          {/* Add-custom footer when the typed string is novel. */}
          {q && !hasExactMatch ? (
            <>
              <div
                style={{
                  height: 1,
                  background: 'var(--border)',
                  margin: '4px 4px',
                }}
              />
              <button
                type="button"
                onClick={() => handlePick(query.trim())}
                style={{
                  width: '100%',
                  textAlign: 'left',
                  background: 'var(--brand-bg)',
                  color: 'var(--brand)',
                  border: 'none',
                  borderRadius: 6,
                  padding: '10px 12px',
                  fontSize: 13,
                  fontWeight: 600,
                  cursor: 'pointer',
                  fontFamily: 'inherit',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                }}
              >
                <i className="ph-bold ph-plus-circle" />
                Add "{query.trim()}" as room
              </button>
            </>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

// ───── Internals ─────

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        padding: '8px 10px 4px',
        fontSize: 10,
        fontWeight: 700,
        color: 'var(--text-tertiary)',
        letterSpacing: 0.6,
        textTransform: 'uppercase',
      }}
    >
      {children}
    </div>
  );
}

function RoomRow({
  room,
  active,
  icon,
  onPick,
}: {
  room: string;
  active: boolean;
  icon: string;
  onPick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onPick}
      // mousedown fires before blur on the input — pick the room
      // before the popover's outside-click handler closes us.
      onMouseDown={(e) => e.preventDefault()}
      style={{
        width: '100%',
        textAlign: 'left',
        background: active ? 'var(--brand-bg)' : 'transparent',
        color: active ? 'var(--brand)' : 'var(--text-primary)',
        border: 'none',
        borderRadius: 6,
        padding: '8px 10px',
        fontSize: 13,
        fontWeight: active ? 600 : 500,
        cursor: 'pointer',
        fontFamily: 'inherit',
        display: 'flex',
        alignItems: 'center',
        gap: 8,
      }}
      onMouseEnter={(e) => {
        if (!active) e.currentTarget.style.background = '#F9FAFB';
      }}
      onMouseLeave={(e) => {
        if (!active) e.currentTarget.style.background = 'transparent';
      }}
    >
      <i
        className={icon}
        style={{
          fontSize: 14,
          color: active ? 'var(--brand)' : 'var(--text-tertiary)',
          width: 16,
          textAlign: 'center',
        }}
      />
      <span style={{ flex: 1 }}>{room}</span>
      {active ? (
        <i
          className="ph-fill ph-check-circle"
          style={{ fontSize: 16, color: 'var(--brand)' }}
        />
      ) : null}
    </button>
  );
}
